import React, { useEffect, useState } from "react";
import "./PLOArticle.css";
import GuideHeader from '../components/GuideHeader';

type Suit = "s" | "h" | "d" | "c";

type PokerCardProps = {
  rank: string;
  suit: Suit;
  selected?: boolean;
  muted?: boolean;
  compact?: boolean;
};

const SUITS: Record<Suit, { symbol: string; label: string; red: boolean }> = {
  s: { symbol: "♠", label: "スペード", red: false },
  h: { symbol: "♥", label: "ハート", red: true },
  d: { symbol: "♦", label: "ダイヤ", red: true },
  c: { symbol: "♣", label: "クラブ", red: false },
};

function PokerCard({ rank, suit, selected = false, muted = false, compact = false }: PokerCardProps) {
  const meta = SUITS[suit];
  return (
    <span
      className={`p-card ${meta.red ? "is-red" : ""} ${selected ? "is-selected" : ""} ${muted ? "is-muted" : ""} ${compact ? "is-compact" : ""}`}
      aria-label={`${rank}${meta.label}`}
    >
      <span className="p-card__rank">{rank}</span>
      <span className="p-card__suit">{meta.symbol}</span>
    </span>
  );
}

function CardRow({ children, label }: { children: React.ReactNode; label?: string }) {
  return (
    <div className="card-row-wrap">
      {label && <div className="card-row-label">{label}</div>}
      <div className="card-row">{children}</div>
    </div>
  );
}

const sections = [
  ["intro", "PLOとは"],
  ["core", "最初に覚える3つ"],
  ["flow", "ゲームの流れ"],
  ["two-three", "手札2枚＋ボード3枚"],
  ["points", "PLOのポイント"],
  ["starting", "スターティングハンド"],
  ["potlimit", "ポットリミット"],
  ["difference", "ホールデムとの違い"],
];

export default function PLOArticle() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  return (
    <div className="plo-page">
      <GuideHeader />

      <main>
        <section className="article-hero">
          <div className="article-hero__inner">
            <div className="hero-copy">
              <nav className="breadcrumb" aria-label="パンくず"><a href="/">トップ</a><span>›</span><a href="/rules">ポーカー各種ルール</a><span>›</span><span>PLO</span></nav>
              <p className="eyebrow">POKER RULES / PLO</p>
              <h1>PLO（ポットリミットオマハ）<br className="desktop-only" />のルール</h1>
              <p className="hero-lead">ホールデム経験者なら、まずは<strong>「4枚配られる」「必ず2枚＋3枚」「ポットリミット」</strong>の3点だけ。<br />プレイ中に見返せるよう、迷いやすい部分を具体例中心でまとめました。</p>
            </div>
            <div className="hero-hand" aria-label="ダブルスーテッドの例 Aスペード Kスペード Qハート Jハート">
              <div className="hero-hand__caption"><span>EXAMPLE</span> ダブルスーテッド</div>
              <div className="hero-hand__cards">
                <PokerCard rank="A" suit="s" />
                <PokerCard rank="K" suit="s" />
                <PokerCard rank="Q" suit="h" />
                <PokerCard rank="J" suit="h" />
              </div>
              <p><b>♠が2枚、♥が2枚。</b><br />「4枚同じスート」ではなく、2枚ずつ2組ある形をダブルスーテッドと呼びます。</p>
            </div>
          </div>
        </section>

        <div className="article-shell">
          <aside className="toc" aria-label="目次">
            <div className="toc__title">CONTENTS</div>
            <ol>{sections.map(([id, label], i) => <li key={id}><a href={`#${id}`}><span>{String(i + 1).padStart(2, "0")}</span>{label}</a></li>)}</ol>
            <div className="toc__note">このページだけで<br />そのまま1ゲーム始められる内容です。</div>
          </aside>

          <article className="article-body">
            <section id="intro" className="section-block">
              <div className="section-head"><span>01</span><div><p>ABOUT</p><h2>PLOとは？</h2></div></div>
              <p className="section-intro">PLO（Pot Limit Omaha）は、各プレイヤーに<strong>4枚のホールカード</strong>が配られるオマハの代表的な形式です。テーブルにはホールデムと同じ5枚のコミュニティカードが出ます。</p>
              <div className="compare-strip">
                <div><small>TEXAS HOLD'EM</small><b>手札 2枚</b><CardRow><PokerCard rank="A" suit="s" compact /><PokerCard rank="K" suit="h" compact /></CardRow></div>
                <div className="compare-strip__arrow">→</div>
                <div className="is-plo"><small>POT LIMIT OMAHA</small><b>手札 4枚</b><CardRow><PokerCard rank="A" suit="s" compact /><PokerCard rank="K" suit="s" compact /><PokerCard rank="Q" suit="h" compact /><PokerCard rank="J" suit="h" compact /></CardRow></div>
              </div>
            </section>

            <section id="core" className="section-block">
              <div className="section-head"><span>02</span><div><p>THE 3 RULES</p><h2>最初にこれだけ覚えればOK</h2></div></div>
              <div className="rule-list">
                <div className="rule-item"><b>1</b><div><h3>手札は4枚</h3><p>全員に4枚ずつ配られます。4枚すべてを最終役に使うわけではありません。</p></div></div>
                <div className="rule-item"><b>2</b><div><h3>役は「手札2枚 ＋ ボード3枚」</h3><p>必ず手札の2枚とボードの3枚を使います。手札1枚だけ、手札3枚以上は使えません。</p></div></div>
                <div className="rule-item"><b>3</b><div><h3>ベット・レイズには上限がある</h3><p>PLOはポットリミット。ノーリミットのように常に好きな額を賭けられるわけではありません。</p></div></div>
              </div>
            </section>

            <section id="flow" className="section-block">
              <div className="section-head"><span>03</span><div><p>GAME FLOW</p><h2>ゲームの流れ</h2></div></div>
              <p className="section-intro">ブラインド、プリフロップ、フロップ、ターン、リバー、ショーダウンという流れはホールデムと同じです。</p>
              <div className="flow-line">
                <div className="flow-step"><i>1</i><b>プリフロップ</b><CardRow><PokerCard rank="?" suit="s" compact muted /><PokerCard rank="?" suit="s" compact muted /><PokerCard rank="?" suit="s" compact muted /><PokerCard rank="?" suit="s" compact muted /></CardRow><p>4枚の手札が配られ、最初のベッティング。</p></div>
                <div className="flow-step"><i>2</i><b>フロップ</b><CardRow><PokerCard rank="Q" suit="s" compact /><PokerCard rank="J" suit="s" compact /><PokerCard rank="10" suit="d" compact /></CardRow><p>ボード3枚を公開。</p></div>
                <div className="flow-step"><i>3</i><b>ターン</b><CardRow><PokerCard rank="Q" suit="s" compact /><PokerCard rank="J" suit="s" compact /><PokerCard rank="10" suit="d" compact /><PokerCard rank="4" suit="c" compact /></CardRow><p>4枚目を公開。</p></div>
                <div className="flow-step"><i>4</i><b>リバー</b><CardRow><PokerCard rank="Q" suit="s" compact /><PokerCard rank="J" suit="s" compact /><PokerCard rank="10" suit="d" compact /><PokerCard rank="4" suit="c" compact /><PokerCard rank="2" suit="h" compact /></CardRow><p>5枚目を公開し、最後のベッティング。</p></div>
              </div>
            </section>

            <section id="two-three" className="section-block important-section">
              <div className="section-head"><span>04</span><div><p>MOST IMPORTANT</p><h2>必ず「手札2枚 ＋ ボード3枚」で役を作る</h2></div></div>
              <p className="section-intro">ここがPLOで最も間違えやすいところです。下の例では、選ばれている<strong>5枚だけ</strong>が最終的な役になります。</p>
              <div className="two-three-demo">
                <div>
                  <CardRow label="あなたの手札（4枚）">
                    <PokerCard rank="A" suit="s" selected /><PokerCard rank="K" suit="s" selected /><PokerCard rank="Q" suit="h" muted /><PokerCard rank="J" suit="h" muted />
                  </CardRow>
                  <div className="selection-note hand-note">使うのは <b>2枚だけ</b></div>
                </div>
                <div className="plus">＋</div>
                <div>
                  <CardRow label="ボード（5枚）">
                    <PokerCard rank="Q" suit="s" selected /><PokerCard rank="J" suit="s" selected /><PokerCard rank="10" suit="d" selected /><PokerCard rank="4" suit="c" muted /><PokerCard rank="2" suit="h" muted />
                  </CardRow>
                  <div className="selection-note board-note">使うのは <b>3枚だけ</b></div>
                </div>
              </div>
              <div className="result-line"><span>完成する役</span><b>A-K-Q-J-10 のストレート</b><em>※ボードの♠が2枚なので、この例ではフラッシュではありません。</em></div>

              <div className="mistake-grid">
                <div className="mistake-box is-ng"><div className="mistake-box__label">NG</div><h3>手札を1枚しか使わない</h3><p>ホールデムの感覚で「A♠だけ使って…」は不可。PLOでは必ず手札2枚です。</p></div>
                <div className="mistake-box is-ok"><div className="mistake-box__label">OK</div><h3>手札2枚・ボード3枚を固定</h3><p>最初に「2＋3」と覚えておけば、フラッシュやストレートの勘違いを大幅に減らせます。</p></div>
              </div>
            </section>

            <section id="points" className="section-block">
              <div className="section-head"><span>05</span><div><p>KEY POINTS</p><h2>PLOで特に押さえたいポイント</h2></div></div>
              <div className="point-list">
                <div className="point-row"><span className="point-index">①</span><div><h3>役ができやすいぶん、相手の役も強くなりやすい</h3><p>4枚から2枚を選べるためドローの組み合わせが増えます。ホールデムで強く見えるワンペアや低いフラッシュを、そのまま強いと判断しないのが大切です。</p></div></div>
                <div className="point-row"><span className="point-index">②</span><div><h3>フラッシュは「自分の同スート2枚」が必要</h3><div className="flush-example"><div className="flush-group"><CardRow label="手札"><PokerCard rank="A" suit="h" compact /><PokerCard rank="K" suit="s" compact /><PokerCard rank="Q" suit="d" compact /><PokerCard rank="J" suit="c" compact /></CardRow></div><span className="flush-plus">＋</span><div className="flush-group"><CardRow label="ボード"><PokerCard rank="9" suit="h" compact /><PokerCard rank="7" suit="h" compact /><PokerCard rank="4" suit="h" compact /><PokerCard rank="2" suit="h" compact /><PokerCard rank="10" suit="c" compact /></CardRow></div></div><p className="flush-note">ボードに♥が4枚あっても、手札にはA♥の1枚しかないため、フラッシュにはできません。<strong>この場合、完成する役はAハイ（AK1097）です。</strong></p></div></div>
                <div className="point-row"><span className="point-index">③</span><div><h3>「ナッツ」を強く意識する</h3><p>強い役同士がぶつかりやすいため、「自分は強い」ではなく「このボードで作れる最強の役は何か」を考える癖が重要です。</p></div></div>
                <div className="point-row"><span className="point-index">④</span><div><h3>4枚が互いに働くハンドを選ぶ</h3><p>4枚あるから何でも参加、ではなく、スート・連続性・高いカード・ペアが複数の組み合わせを作れるハンドほど扱いやすくなります。</p></div></div>
              </div>
            </section>

            <section id="starting" className="section-block">
              <div className="section-head"><span>06</span><div><p>STARTING HANDS</p><h2>初心者が見分けやすい「まとまりのある4枚」</h2></div></div>
              <p className="section-intro">「強さの順位表」ではなく、4枚同士がどれだけ連携しているかを見るための例です。</p>
              <div className="hand-examples">
                <div className="hand-example"><div className="hand-example__top"><span>DOUBLE-SUITED</span><b>ダブルスーテッド</b></div><CardRow><PokerCard rank="A" suit="s" /><PokerCard rank="K" suit="s" /><PokerCard rank="Q" suit="h" /><PokerCard rank="J" suit="h" /></CardRow><p><strong>♠2枚＋♥2枚。</strong>2つのスートでフラッシュドローを作れる形。</p></div>
                <div className="hand-example"><div className="hand-example__top"><span>CONNECTED</span><b>連続性が高い</b></div><CardRow><PokerCard rank="J" suit="s" /><PokerCard rank="10" suit="d" /><PokerCard rank="9" suit="c" /><PokerCard rank="8" suit="h" /></CardRow><p>4枚のランクが近く、複数方向のストレートを作りやすい形。</p></div>
                <div className="hand-example"><div className="hand-example__top"><span>HIGH PAIR + SUPPORT</span><b>高いペア＋つながり</b></div><CardRow><PokerCard rank="A" suit="s" /><PokerCard rank="A" suit="h" /><PokerCard rank="K" suit="s" /><PokerCard rank="Q" suit="h" /></CardRow><p>AAだけでなくK・Qも連携し、さらに♠と♥の2組がある形。</p></div>
              </div>
            </section>

            <section id="potlimit" className="section-block">
              <div className="section-head"><span>07</span><div><p>POT LIMIT</p><h2>ポットリミットの計算</h2></div></div>
              <p className="section-intro">誰もまだベットしていない場面なら、最大ベットはその時点のポット額。すでに相手のベットがある場合は、まずコールしたと仮定してから最大レイズ額を計算します。</p>
              <div className="pot-subhead"><span>例1</span><h3>相手のベットに対してポットまでレイズする場合</h3></div>
              <div className="pot-example">
                <div className="pot-example__given"><span>例1</span><p>ベット前のポット <b>1,000</b><br />相手が <b>300</b> ベット<br />あなたのコール額 <b>300</b></p></div>
                <div className="pot-example__calc">
                  <small>コール後にできるポット</small>
                  <strong>1,000 ＋ 300 ＋ 300 ＝ 1,600</strong>
                  <div className="calc-divider"></div>
                  <small>最大レイズは「1,600追加」できる</small>
                  <strong className="gold">あなたの合計額は 1,900</strong>
                </div>
              </div>
              <div className="pot-tip"><span>イメージ</span><p>コールとベットを同時に行うイメージ</p></div>

              <div className="pot-subhead pot-subhead--second"><span>例2</span><h3>プリフロップでポットサイズまでオープンレイズする場合</h3></div>
              <p className="preflop-caption">ブラインド100 / 200、アンティなし。全員フォールドで自分まで回ってきた場面です。</p>
              <div className="preflop-grid">
                <div className="preflop-card">
                  <div className="preflop-card__title"><b>BTNからオープン</b><span>最大 700（3.5BB）</span></div>
                  <div className="preflop-formula"><small>現在のポット</small><strong>SB 100 ＋ BB 200 ＝ 300</strong><small>BB 200をコールしたと仮定</small><strong>300 ＋ 200 ＝ 500</strong><small>その500まで追加レイズできる</small><strong className="gold">200 ＋ 500 ＝ 700</strong></div>
                </div>
                <div className="preflop-card">
                  <div className="preflop-card__title"><b>SBからオープン</b><span>最大 600（3BB）</span></div>
                  <div className="preflop-formula"><small>現在のポット</small><strong>SB 100 ＋ BB 200 ＝ 300</strong><small>SBはあと100でBBにコール</small><strong>300 ＋ 100 ＝ 400</strong><small>その400まで追加レイズできる</small><strong className="gold">100（既出）＋100＋400 ＝ 600</strong></div>
                </div>
              </div>
            </section>

            <section id="difference" className="section-block">
              <div className="section-head"><span>08</span><div><p>COMPARISON</p><h2>テキサスホールデムとの違い</h2></div></div>
              <p className="table-scroll-hint">← 横にスワイプして比較できます →</p>
              <div className="comparison-table-wrap" role="region" aria-label="テキサスホールデムとPLOの比較表" tabIndex={0}><table className="comparison-table"><thead><tr><th></th><th>テキサスホールデム</th><th>PLO</th></tr></thead><tbody><tr><th>手札</th><td>2枚</td><td><b>4枚</b></td></tr><tr><th>最終役の作り方</th><td>7枚の中から自由に5枚</td><td><b>手札2枚＋ボード3枚</b></td></tr><tr><th>主なベット形式</th><td>ノーリミットが一般的</td><td><b>ポットリミット</b></td></tr><tr><th>役の傾向</th><td>比較的シンプル</td><td>強い役・強いドローがぶつかりやすい</td></tr></tbody></table></div>
            </section>

            <section className="article-summary">
              <p className="eyebrow">QUICK RECAP</p>
              <h2>卓につく前に、これだけ再確認</h2>
              <div className="summary-line"><span>1</span><b>手札は4枚</b></div>
              <div className="summary-line"><span>2</span><b>必ず手札2枚＋ボード3枚</b></div>
              <div className="summary-line"><span>3</span><b>PLOはポットリミット</b></div>
              <div className="summary-line"><span>4</span><b>ダブルスーテッド＝2枚ずつ同じスートが2組</b></div>
            </section>
          </article>
        </div>

        <section className="article-cta">
          <div className="article-cta__copy">
            <p>AKIHABARA / KANDA · 24H</p>
            <h2>PLOも、ポーカーレンタルスペース デュエルの貸卓で。</h2>
            <span>秋葉原・神田で、ポーカーテーブル・チップ・トランプ・自動シャッフル機を完備。PLOの練習や友人同士のポーカー会にも、手ぶらでご利用いただけます。</span>
          </div>
          <div className="article-cta__actions">
            <a className="article-cta__reserve" href="https://upnow.jp/duel">今すぐ予約する <small>登録不要</small></a>
            <a className="article-cta__internal" href="/#pricing">料金・設備を確認する →</a>
          </div>
        </section>
      </main>
    </div>
  );
}
